import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type Me } from './api';
import { HESAP_METIN } from './metin/hesap';
import { ORTAK, rolAdi, tamSayi } from './metin/ortak';

/**
 * Profil ve Ayarlar.
 *
 * Ürün sahibi hesap menüsünde bu iki maddeyi istedi. Menüye ölü bağlantı koymamak için ikisi de
 * gerçekten bir şey yapıyor: profil uçtan gelen kaydı gösteriyor, ayarlar iki tema arasında
 * seçim yaptırıyor. İkisi de uydurma bir "yakında" ekranı değil.
 */

/** Kendi parolanı değiştir: eski parola istenir, diğer cihazlardaki oturumlar kapanır. */
function ParolaDegistir() {
  const metin = HESAP_METIN.profil;
  const [mevcut, setMevcut] = useState('');
  const [yeni, setYeni] = useState('');
  const [tekrar, setTekrar] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [not, setNot] = useState<string | null>(null);
  const [yapiliyor, setYapiliyor] = useState(false);

  const gonder = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHata(null);
    setNot(null);
    if (yeni !== tekrar) {
      setHata(metin.parolaUyusmuyor);
      return;
    }
    setYapiliyor(true);
    try {
      const sonuc = await api.changeOwnPassword(mevcut, yeni);
      setNot(metin.parolaTamam(sonuc.revokedSessions));
      setMevcut('');
      setYeni('');
      setTekrar('');
    } catch (caught) {
      setHata(caught instanceof ApiError ? caught.message : metin.parolaHata);
    } finally {
      setYapiliyor(false);
    }
  };

  return (
    <form className="user-create" data-testid="parola-degistir" onSubmit={gonder}>
      <h3>{metin.parolaBaslik}</h3>
      <p className="muted">{metin.parolaTarif}</p>
      <div className="user-create-fields">
        <label>
          {metin.parolaMevcut}
          <input type="password" autoComplete="current-password" required data-testid="parola-mevcut" value={mevcut} onChange={(event) => setMevcut(event.target.value)} />
        </label>
        <label>
          {metin.parolaYeni}
          <input type="password" autoComplete="new-password" required minLength={12} maxLength={200} data-testid="parola-yeni" value={yeni} onChange={(event) => setYeni(event.target.value)} />
        </label>
        <label>
          {metin.parolaTekrar}
          <input type="password" autoComplete="new-password" required minLength={12} maxLength={200} data-testid="parola-yeni-tekrar" value={tekrar} onChange={(event) => setTekrar(event.target.value)} />
        </label>
        <button type="submit" className="primary" data-testid="parola-degistir-gonder" disabled={yapiliyor}>
          {yapiliyor ? metin.parolaKaydediliyor : metin.parolaKaydet}
        </button>
      </div>
      <p className="muted">{metin.parolaKural}</p>
      {hata ? <div className="error callout" role="alert" data-testid="parola-degistir-hata">{hata}</div> : null}
      {not ? <div className="callout callout-ok" role="status" data-testid="parola-degistir-tamam">{not}</div> : null}
    </form>
  );
}

export function ProfilePage({ me }: { me: Me }) {
  const metin = HESAP_METIN.profil;
  return (
    <section id="profil" className="content-section" data-testid="profil" aria-labelledby="profil-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="profil-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{metin.yetkiSayisi(tamSayi(me.permissions.length))}</span>
      </div>
      <div className="table-wrap">
        <table data-testid="profil-tablo">
          <tbody>
            <tr><th scope="row">{metin.ad}</th><td data-testid="profil-ad">{me.displayName}</td></tr>
            {/*
              E-posta burada **yok** çünkü uç göndermiyor: `GET /auth/me` bugün `userId`,
              `displayName`, `role` ve `permissions` döndürüyor. Uydurulmuş ya da tahmin edilmiş
              bir e-posta, olmayan bir alanı varmış gibi göstermek olurdu.
            */}
            <tr><th scope="row">{metin.kullaniciKimligi}</th><td><code>{me.userId}</code></td></tr>
            <tr><th scope="row">{metin.rol}</th><td>{rolAdi(me.role)}</td></tr>
            {/* Kod değeri ekranda da duruyor: bir yetki sorununu konuşurken ekipte anılan ad bu. */}
            <tr><th scope="row">{metin.rolKodu}</th><td><code>{me.role}</code> <small className="muted">{metin.kodNotu}</small></td></tr>
          </tbody>
        </table>
      </div>
      <h3>{metin.yetkiler}</h3>
      {me.permissions.length === 0 ? (
        <p className="muted">{metin.yetkiYok}</p>
      ) : (
        <div className="filters" data-testid="profil-yetkiler">
          {me.permissions.map((yetki) => <span className="state" key={yetki}>{yetki}</span>)}
        </div>
      )}
      <ParolaDegistir />
    </section>
  );
}

/** Tema seçimi. `sistem` hiçbir öznitelik yazmıyor, yani `prefers-color-scheme` geçerli kalıyor. */
const TEMALAR = ['sistem', 'acik', 'karanlik'] as const;
type Tema = (typeof TEMALAR)[number];
const SAKLAMA_ANAHTARI = 'mbd-crm-tema';

function saklananTema(): Tema {
  const deger = window.localStorage.getItem(SAKLAMA_ANAHTARI);
  return TEMALAR.includes(deger as Tema) ? (deger as Tema) : 'sistem';
}

/**
 * Seçimi belgeye uygular.
 *
 * `marka.css` üç blok tanımlıyor: `:root` açık, `prefers-color-scheme: dark` sistem karanlıkken,
 * `[data-theme]` kullanıcının açık seçimi. Burada yapılan tek şey o üçüncü bloğu açmak ya da
 * kapatmak; renkler yine paletin kendisinden geliyor.
 */
export function temayiUygula(tema: Tema): void {
  if (tema === 'sistem') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', tema === 'karanlik' ? 'dark' : 'light');
}

/** Açılışta saklanan seçimi uygular. Sayfa çizilmeden çağrılıyor, yoksa tema bir an titrer. */
export function temayiBaslat(): void {
  temayiUygula(saklananTema());
}

export function SettingsPage() {
  const metin = HESAP_METIN.ayarlar;
  const [tema, setTema] = useState<Tema>(() => saklananTema());

  useEffect(() => {
    window.localStorage.setItem(SAKLAMA_ANAHTARI, tema);
    temayiUygula(tema);
  }, [tema]);

  const secenekler: { deger: Tema; etiket: string }[] = [
    { deger: 'sistem', etiket: metin.temaSistem },
    { deger: 'acik', etiket: metin.temaAcik },
    { deger: 'karanlik', etiket: metin.temaKaranlik },
  ];

  return (
    <section id="ayarlar" className="content-section" data-testid="ayarlar" aria-labelledby="ayarlar-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="ayarlar-title">{metin.baslik}</h2>
        </div>
      </div>
      <h3>{metin.temaBaslik}</h3>
      <p className="muted">{metin.temaTarif}</p>
      <div className="filters" role="radiogroup" aria-label={metin.temaEtiketi}>
        {secenekler.map((secenek) => (
          <label key={secenek.deger}>
            <input
              type="radio"
              name="tema"
              data-testid={`tema-${secenek.deger}`}
              value={secenek.deger}
              checked={tema === secenek.deger}
              onChange={() => setTema(secenek.deger)}
            />
            {secenek.etiket}
          </label>
        ))}
      </div>
      <p className="source">{ORTAK.durum}: {secenekler.find((s) => s.deger === tema)?.etiket}</p>
    </section>
  );
}
